import { FC, useEffect, useState } from 'react'
import { endOfMonth, startOfMonth } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { CloseStoreCalendarContainer } from 'components/Calendar'
import { Spacer } from 'components/Utility'
import { State } from 'index'
import { getModalEdited, getModalError } from 'reducks/modal/selectors'
import { Caution } from 'components/Typography'
import { OrangeButton } from 'components/Button'
import { useSelectStoreCalendar } from 'hooks/storeCalendar'
import { useSkip } from 'hooks/effect'
import { closeModal, editContentInModal } from 'reducks/modal/operations'
import { StoreCalendarType } from 'types/storeCalendar'
import { getStoreCalendars, getStoreCurrentMonth } from 'reducks/storeCalendar/selectors'
import { saveStoreCalendarsConfirm } from 'reducks/confirm/operations'
import { getHolidays } from 'reducks/store/selectors'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchFutureStocks } from 'reducks/stock/operations'
import { fetchOrdersInPeriod, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { fetchClosedDates } from 'reducks/calendar/operations'

const CloseCalendarModal: FC = () => {
  const dispatch = useDispatch()

  const edited = useSelector((state: State) => getModalEdited(state))
  const modalError = useSelector((state: State) => getModalError(state))
  const currentMonth = useSelector((state: State) => getStoreCurrentMonth(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const currentStoreCalendars = useSelector((state: State) => getStoreCalendars(state))

  const [storeCalendars, setStoreCalendars] = useState<StoreCalendarType[]>(currentStoreCalendars)
  const handleStoreCalendars = useSelectStoreCalendar(setStoreCalendars)

  // 初回レンダリングと2回目のレンダリング(臨時休業日/営業日の更新)で編集済みフラグの変更をスキップ
  useSkip(
    () => {
      dispatch(editContentInModal())
    },
    [storeCalendars],
    1
  )

  // 当日以降の在庫を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: ['preorder'] }))
  }, [storeRef])

  // 表示月が変わったら注文を取得する
  useEffect(() => {
    if (!currentMonth || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrdersInPeriod(
        startOfMonth(currentMonth),
        endOfMonth(currentMonth),
        'deliver',
        storeRef,
        ['succeeded', 'requires_capture'],
        ['daily', 'preorder']
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [currentMonth, storeRef])

  // 休業日を取得
  useEffect(() => {
    if (!currentMonth || !storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, storeRef, holidays, specialHolidays])

  return (
    <div>
      <CloseStoreCalendarContainer handleStoreCalendars={handleStoreCalendars} storeCalendars={storeCalendars} />
      {modalError !== '' && (
        <>
          <Spacer size={16} />
          <Caution caution={modalError} />
        </>
      )}
      <Spacer size={16} />
      <OrangeButton
        disabled={!edited}
        label={'変更内容を確認する'}
        onClick={() => {
          dispatch(saveStoreCalendarsConfirm(storeCalendars, 'close'))
          dispatch(closeModal())
        }}
        size={'medium'}
        type={'main'}
      />
    </div>
  )
}

export default CloseCalendarModal
